import React, { FC } from 'react';
import styled from 'styled-components';

const ACCENT = '#3085d6';
const ERROR = '#c0392b';
const MUTED = '#6b7177';

// The server keeps the same length
// (OrderSubmit::RetouchingPurchasedItemsBuilder::NOTE_MAX_LENGTH).
export const RETOUCHING_NOTE_MAX_LENGTH = 250;

// A hairline sets the note off from the photo (and from the level choices
// above it, when there are several), so the tile reads as photo, then its
// retouching, then its note.
const Field = styled.div`
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #e9ecef;
`;

const Label = styled.label`
  display: block;
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  color: #222;
`;

// "required" keeps the label's color so it is not read past; "optional"
// steps back.
const Qualifier = styled.span<{ $required: boolean }>`
  font-weight: 400;
  color: ${(props) => (props.$required ? '#4a4a4a' : '#6b7177')};
`;

const Input = styled.textarea<{ $invalid: boolean }>`
  display: block;
  width: 100%;
  min-height: 76px;
  padding: 8px 10px;
  border: 1px solid ${(props) => (props.$invalid ? ERROR : '#c9ced3')};
  border-radius: 6px;
  background: ${(props) => (props.$invalid ? '#fff8f7' : '#fff')};
  box-shadow: none;
  font: inherit;
  font-size: 14px;
  line-height: 1.45;
  color: #222;
  resize: vertical;

  &::placeholder {
    color: #8a9096;
  }

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$invalid ? ERROR : ACCENT)};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.$invalid
          ? 'rgba(192, 57, 43, 0.16)'
          : 'rgba(48, 133, 214, 0.18)'};
  }
`;

const NoteError = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  color: ${ERROR};
`;

// The disclaimer and the character count share one quiet line under the box.
const Footer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  color: ${MUTED};
`;

const Count = styled.span`
  flex: none;
  font-size: 12px;
  line-height: 1.4;
  color: ${MUTED};
  font-variant-numeric: tabular-nums;
`;

interface RetouchingPhotoNoteProps {
  poseId: number;
  photoName: string;
  mode: 'optional' | 'required';
  value: string;
  invalid: boolean;
  onChange: (value: string) => void;
}

// The parent's note for the retoucher on one selected photo, shown whenever
// the photo's level takes notes (optional or required).
const RetouchingPhotoNote: FC<RetouchingPhotoNoteProps> = ({
  poseId,
  photoName,
  mode,
  value,
  invalid,
  onChange,
}) => {
  const id = `retouching-note-${poseId}`;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const required = mode === 'required';

  return (
    <Field>
      <Label htmlFor={id}>
        Note for {photoName}{' '}
        <Qualifier $required={required}>
          ({required ? 'required' : 'optional'})
        </Qualifier>
      </Label>
      <Input
        id={id}
        rows={3}
        maxLength={RETOUCHING_NOTE_MAX_LENGTH}
        placeholder="Anything the retoucher should know about this photo"
        value={value}
        $invalid={invalid}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? `${errorId} ${hintId}` : hintId}
        onChange={(event) => onChange(event.target.value)}
      />
      {invalid && (
        <NoteError id={errorId}>Add a note for this photo.</NoteError>
      )}
      <Footer>
        <Hint id={hintId}>Not all requests can be accommodated.</Hint>
        {/* The box's maxLength already tells assistive technology the limit. */}
        <Count aria-hidden="true">
          {value.length}/{RETOUCHING_NOTE_MAX_LENGTH}
        </Count>
      </Footer>
    </Field>
  );
};

export default RetouchingPhotoNote;
